import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom, Observable, of, Subject } from 'rxjs';
import { Languages } from '../models/languages';
import { ISettings } from '../models/settings';
import { PHPermissions } from '../models/PHPermissions';
import { catchError, map, tap } from 'rxjs/operators';
import { IPersonalSettings, PersonalSettings } from '../models/personalSettings';
import { SnackBarService } from './snack-bar.service';
import { ServiceExtensions } from '../models/serviceExtensions';
import { ServiceFeatures } from '../models/serviceFeatures';
import { LimitationsCodeNames } from '../models/limitationsCodeNames';
import { GlobalSettings } from '../models/globalSettings';
import * as Sentry from '@sentry/browser';
import { IConfirmModalAnswer } from '../modals/confirm-modal/models/confirmModalAnswer';
import { ConfirmModalComponent } from '../modals/confirm-modal/confirm-modal.component';
import { IConfirmModalData } from '../modals/confirm-modal/models/confirmModalData';
import { MatDialog } from '@angular/material/dialog';
import { IDefaults, IDefaultsResponse } from '../models/defaults';
import { ILimitation } from '../models/limitation';
import { ITariffPlan } from '../models/tariffPlan';
import { IInitialData, IInitialResponse } from '../models/initial';
import { PlatformApplications } from '../models/platformApplications';
import { IApplication } from '../models/application';
import * as uuid from 'uuid';
import { MeasurementSystem } from '../models/measurementSystem';
import { UnitConverter } from '../utils/unitConverter';
import { Utils } from './utils.service';
import { formatDate } from '@angular/common';
import { IOpenChatEvent } from '../models/chat';

export type Client = {
	id: number;
	is_active: boolean;
	name: string;
}

export type Tokens = {
	accessToken: string;
	refreshToken: string;
}

export type ListsColumnsKey =
	'planogramListColumns' |
	'planListColumns' |
	'equipmentListColumns' |
	'productListColumns' |
	'productLayoutTable' |
	'planogramEditProductsColumns' |
	'addProductsTableColumns';

export const OUTSIDE_APP_HEADER: string = 'X-Outside-App-Domain';

@Injectable({
	providedIn: 'root'
})
export class SessionService {

	private _apps: Array<IApplication> = [];
	private _clients: Array<Client> = [];
	private _isFramePage: boolean = false;
	private _media: { [key: string]: { media_type: string; url: string } };
	private _permissions: Array<PHPermissions | ServiceFeatures>;
	clientId: number;
	clientName: string;
	clientIdChange: BehaviorSubject<number> = new BehaviorSubject<number>(-1);
	demoDaysUsed$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
	demoDaysLeft$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
	demoDaysTotal$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
	demoDaysLeftPercentage$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
	defaults: IDefaults;
	hasExpired$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	language: Languages;
	limitations: { [key in LimitationsCodeNames]: BehaviorSubject<ILimitation> } = {
		[LimitationsCodeNames.StorePlans]: new BehaviorSubject<ILimitation>(null),
		[LimitationsCodeNames.Planograms]: new BehaviorSubject<ILimitation>(null),
		[LimitationsCodeNames.Products]: new BehaviorSubject<ILimitation>(null),
		[LimitationsCodeNames.PlanogramCompletionsDownload]: new BehaviorSubject<ILimitation>(null)
	};
    openChat$: Subject<IOpenChatEvent> = new Subject<IOpenChatEvent>();
	personalSettings: PersonalSettings = new PersonalSettings();
	personalSettingsShownSubject: Subject<boolean> = new Subject<boolean>();
    sessionId: string;
	token: string;
	username: string;
	private _webglWorking: boolean | null = null;

	get apps(): Array<IApplication> {
		return this._apps;
	}

	set apps(value: Array<IApplication>) {
		this._apps = value;
	}

	get clients(): Array<Client> {
		return this._clients;
	}

	set clients(clients: Array<Client>) {
		this._clients = clients;
	}

	get isFramePage(): boolean {
		return this._isFramePage;
	}

	get media(): { [key: string]: { media_type: string; url: string } } {
		return this._media;
	}

	set media(value: { [key: string]: { media_type: string; url: string } }) {
		this._media = value;
	}

	get webglWorking(): boolean {
		return this._webglWorking;
	}

	get defaultRoute(): { title: string, route: string } {
		if (this.defaults.isManager) {
			if (this.hasFeature(ServiceFeatures.PlanogramsExecution)) {
				return { title: 'NAVIGATION.TASKS', route: 'tasks' };
			} else  {
				return null;
			}
		}

		if (this.hasFeature(ServiceFeatures.Tasks)) {
			return { title: 'NAVIGATION.TASKS', route: 'dashboard' };
		}

		if (this.hasFeature(ServiceFeatures.Demo)) {
			return { title: 'NAVIGATION.HOME_PAGE', route: 'home' };
		}

		if (this.hasFeature(ServiceFeatures.Welcome)) {
			return { title: 'NAVIGATION.HOME_PAGE', route: 'welcome' };
		}

		return null;
	}

	get settings(): Partial<ISettings> {
		return this.defaults.settings;
	}

	private static isMobile(): boolean {
		const userAgent: string = navigator.userAgent;
		return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
	}

	static isStaff(name: string): boolean {
		return name.endsWith('@datawiz.io') || name.endsWith('@planohero.com') || name.startsWith('support_');
	}

	static isPH(name: string): boolean {
		return name.endsWith('@datawiz.io') || name.endsWith('@planohero.com');
	}

	static isNIQ(name: string): boolean {
		return name.endsWith('@nielsen.com') || name.endsWith('@nielseniq.com');
	}

	static isInnerClient(id: number): boolean {
		const innerClients: Array<number> = [148, 227, 248, 273, 280, 1397, 1398, 1399, 1400, 1401, 1402, 1403];

		return innerClients.includes(id);
	}

	constructor(
		private dialog: MatDialog,
		private http: HttpClient,
		private snackBarService: SnackBarService,
        @Inject(LOCALE_ID) private _locale: string
	) {
        this.sessionId = String(uuid.v4());
		this.username = localStorage.getItem('username') || '';
		this.token = localStorage.getItem('token') || '';
		this.language = localStorage.getItem('language') as Languages || Languages.English;
		this.defaults = JSON.parse(localStorage.getItem('defaults')) || {};
		if (this.defaults.hasOwnProperty('permission')) this.setPermissions();
	}

	private setPermissions(): void {
		this._permissions = this.defaults.permissions;
	}

	private loadApplications(): Observable<Array<IApplication>> {
		return this.http.get<Array<IApplication>>('/api/applications/');
	}

	private loadDefaults(): Observable<IDefaultsResponse> {
		return this.http.get<IDefaultsResponse>('api/defaults/');
	}

	private loadInitial(): Observable<IInitialResponse> {
		return this.http
			.get<IInitialResponse>('api/initial/')
			.pipe(map((response: IInitialResponse) => {
				response.clients.sort((a: Client) => {
					if (SessionService.isNIQ(response.email) && a.id === 248) return -1; // 248 - RetailerX
					if (SessionService.isStaff(response.email) && a.id === 148) return -1;  // 148 - MegaTest
					return 1;
				});
				return response;
			}));
	}

	private loadMedia(): Observable<any> {
		return this.http.get<any>('api/media/');
	}

	private parseInitialResponse(defaults: IDefaultsResponse, initial: IInitialResponse): IInitialData {
		const tariffPlan: ITariffPlan = defaults.tariff_plans
			.find((tariff: ITariffPlan) => tariff.app_id == GlobalSettings.APP_ID);

		return {
			username: initial.email,
			defaults: {
				announcements: defaults.announcements || [],
				settings: initial.app_config,
				personal_settings: defaults.personal_settings,
				date_from: defaults.date_from,
				date_to: defaults.date_to,
				user_id: initial.user_id,
				email: initial.email,
				role: defaults.role,
				fullName: `${initial.first_name} ${initial.last_name}`,
				image: initial.photo,
				permissions: defaults.permissions,
				extensions: defaults.client_info.extensions,
                country: defaults.client_info.country,
				tariff_plan: tariffPlan,
				demo: defaults.demo,
				root_category: defaults.client_info.root_category?.id,
				root_marker: defaults.client_info.root_marker?.id,
				has_assortment: defaults.client_info?.has_assortment,
				has_categories: defaults.client_info?.has_categories,
				max_category_levels: defaults.client_info?.max_category_levels,
				has_brands: defaults.client_info?.has_brands,
				has_sales: defaults.client_info?.has_sales,
				isAdmin: defaults.is_admin,
				isManager: defaults.is_shop,
				client_date_format: defaults.client_date_format,
				planogramId: defaults.planogram_id,
                usageTrackingEnabled: defaults.usage_tracking_enabled,
				hasBI: defaults.client_info.has_bi,
				hasCategoryManager: defaults.client_info.has_category_managers,
				measurement_system: defaults.client_info.measurement_system ?? MeasurementSystem.Metrics,
				could_delete_demo_products: defaults.could_delete_demo_products,
				max_products_in_mp: defaults.client_info.max_products_in_mp
			}
		};
	}

	private initializeUsetiful(): void {
		(window as any).usetifulTags = {
			userId: this.defaults.user_id,
			fullName : this.defaults.fullName,
			role : this.defaults.role.name,
			client: this.clientName,
			tariff: this.defaults.tariff_plan.name,
			language : this.language
		};

		const head: HTMLHeadElement = document.getElementsByTagName('head')[0];
		const script: HTMLScriptElement = document.createElement('script');
		script.async = true;
		script.src = 'https://www.usetiful.com/dist/usetiful.js';
		script.setAttribute('id', 'usetifulScript');
		script.dataset.token = environment.usetifulToken;
		head.appendChild(script);
	}

	clearSession(): void {
		localStorage.clear();
		localStorage.language = this.language;
	}

	async initialize(): Promise<void> {
		if (window.location.href.includes('/frame/')) this._isFramePage = true;

		if (window.location.href.includes('landing')) return;
		const initialResponse: IInitialResponse = await lastValueFrom(this.loadInitial());

		let clientId: string | number;
		const matches = window.location.pathname.match(/\/c\/([^\/]+)/);
		if (matches && matches.length > 1) {
			clientId = matches[1];
		} else {
			clientId = localStorage.getItem('client_id');
		}

		if (initialResponse.clients?.every((client) => client.id !== Number(clientId))) {
			this.setUrlClientId(Number(clientId), initialResponse.clients[0].id)
			clientId = initialResponse.clients[0].id;
		}
		this.clients = initialResponse.clients;
		this.setClientId(Number(clientId));

		const defaultsResponse: IDefaultsResponse = await lastValueFrom(this.loadDefaults());
		const initialData: IInitialData = this.parseInitialResponse(defaultsResponse, initialResponse);

		const initialLang: Languages = initialResponse.lang as Languages;
		this.username = initialData.username;
		this.language = Object.values(Languages).includes(initialLang) ? initialLang : Languages.English;
		this.defaults = initialData.defaults;
		document.documentElement.lang = this.language;

		localStorage.setItem('username', initialData.username);
		localStorage.setItem('language', this.language);
		localStorage.setItem('defaults', JSON.stringify(initialData.defaults));

		this.setPermissions();
		this.personalSettings = new PersonalSettings(this.defaults.personal_settings);
		UnitConverter.init(this.defaults.measurement_system);

		for (let key in this.limitations) {
			this.limitations[key].next(
				this.defaults.tariff_plan?.limitations.find((l: ILimitation) => l.codename == key) || null
			);
		}
		this.hasExpired$.next(this.defaults.tariff_plan.has_expired);

		this.apps = await lastValueFrom(this.loadApplications());
		this.media = await lastValueFrom(this.loadMedia());

		this._webglWorking = Utils.testWebGLSupport();
	}

    async initializeModals(): Promise<void> {
        if (!this.isFramePage) {
			Sentry.setUser({
				email: this.defaults.email,
				id: this.defaults.user_id,
				username: this.defaults.fullName
			});
			this.initializeUsetiful();
			this.apps = await lastValueFrom(this.loadApplications());
			this.media = await lastValueFrom(this.loadMedia());
		}

		if (!this.isFramePage && SessionService.isMobile() && !this.defaults.settings.hideMobileWarning) {
			const result: IConfirmModalAnswer = await lastValueFrom(
				this.dialog
					.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
						width: '574px',
						autoFocus: false,
						backdropClass: 'back-drop',
						data: {
							label: 'COMMON.MOBILE_RECOMMENDED',
							confirmLabel: 'COMMON.ROGER_THAT',
							checkBox: true,
							checkBoxLabel: 'NOT_SHOW_ANYMORE'
						}
					})
					.afterClosed());

			if (result.answer && result.checkBoxState) {
				this.defaults.settings.hideMobileWarning = result.answer;
				this.updateSettings();
			}
		}

		if (!this.isFramePage && this.hasFeature(ServiceFeatures.Demo) && !this.defaults.settings.welcomeDemoFormCompleted) {
			const { WelcomeDemoFormModalComponent } = await import('../modals/welcome-demo-form-modal/welcome-demo-form-modal.component');
			this.dialog.open(WelcomeDemoFormModalComponent, {
				width: '900px',
				autoFocus: false,
				backdropClass: 'back-drop',
				disableClose: true,
			}).afterClosed();
		}
    }

	getAvailableTariff(feature: ServiceFeatures): Observable<{ id: number, name: string }> {
		return this.http.get<{ id: number, name: string }>(
			'/api/applications/30/tariff-plans/availability/',
			{ params: new HttpParams({ fromObject: { entity: feature } }) }
		);
	}

	getCurrentTime(): Observable<{ datetime: string }> {
		return this.http.get<{ datetime: string }>('api/time/');
	}

	getDemoExpiresDays(): void {
		this.demoDaysLeft$.next(0);
		this.getCurrentTime()
			.pipe(catchError(() => of({ datetime: (new Date()).toString() })))
			.subscribe((result: { datetime: string }) => {
				const expirationDate: Date = new Date(this.defaults.tariff_plan.active_date_to);
				const startDate: Date = new Date(this.defaults.tariff_plan.active_date_from);
				const currentDate: Date = new Date(result.datetime);
				const nextDate: Date = new Date(result.datetime);

				expirationDate.setDate(expirationDate.getDate() + 1);
				expirationDate.setHours(0, 0, 0);
				nextDate.setDate(currentDate.getDate() + 1);
				nextDate.setHours(0, 0, 0);

				const usedDays: number = Math.ceil((currentDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24));
				const totalDays: number = Math.ceil((expirationDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24));
				this.demoDaysUsed$.next(usedDays);
				this.demoDaysTotal$.next(totalDays);

				if ((expirationDate.getTime() - currentDate.getTime()) >= 0) {
					this.demoDaysLeft$.next(Math.ceil((expirationDate.getTime() - currentDate.getTime()) / (1000 * 3600 * 24)));
					this.demoDaysLeftPercentage$.next((usedDays / totalDays) * 100);
					const timeout: number = nextDate.getTime() - currentDate.getTime();
					setTimeout(() => this.getDemoExpiresDays(), timeout);
				} else {
					this.demoDaysLeftPercentage$.next(100);
					this.demoDaysLeft$.next(0);
				}
			});
	}

	getLink(link: string | Array<string | number>): Array<string | number> {
		if (!Array.isArray(link)) link = [link];
		if (typeof link[0] === 'string' && link[0].indexOf('/') === 0) link[0] = link[0].substring(1);
		if (!link[0]) return ['/c', this.clientId];

		return ['/c', this.clientId, ...link];
	}

	getExternalLink(appId: PlatformApplications, route: string = ''): string {
		const application: IApplication = this.apps.find((app: IApplication) => app.app_id === appId);

		if (application) {
			const url: string = `${application.host}${application.path ?? ''}${route}`;
			return url.replace(':client_id', this.clientId.toString());
		} else {
			//throw new Error('There is no such application');
			console.warn('There is no such application');
			return this.getLink(route).join('/');
		}
	}

	hasAccess(functional: string, additions: Array<string> = []): boolean {
		const accesses: Array<string> = [];

		if (additions.length) {
			additions.forEach((addition: string) => accesses.push(`${functional}-${addition}`));
		} else {
			accesses.push(functional);
		}

		return accesses.some((access: PHPermissions) => {
			return this._permissions.some((permission: PHPermissions) => permission == access);
		});
	}

	hasExtension(extension: ServiceExtensions): boolean {
		return this.defaults.extensions?.includes(extension);
	}

	hasFeature(feature: ServiceFeatures): boolean {
		return this._permissions?.includes(feature);
	}

	hasLimitExceeded(codename: LimitationsCodeNames): Observable<boolean> {
		return this.limitations[codename].pipe(
			map((l: ILimitation) => l?.used >= l?.limit)
		);
	}

	setClientId(clientId: number): void {
		if (this.clientId != clientId) {
			this.clientId = clientId;
			this.clientName = this.clients.find((client: Client) => client.id === clientId)?.name;
			localStorage.setItem('client_id', clientId.toString());
			this.clientIdChange.next(clientId);
		}
	}

	setUrlClientId(oldId: number, clientId: number): void {
		let newUrl: string = window.location.pathname.replace(`/c/${oldId}/`, `/c/${clientId}/`);
		if (window.location.search) newUrl += window.location.search;
		window.history.replaceState(null, '', newUrl);
	}

	setTokens(tokens: Tokens): void {
		this.token = tokens.accessToken;
	}

    formatDate(date: string | Date, format: string): string {
        return formatDate(date, format, this._locale);
    }

    openDemoPresentaionModal(): void {
        this.dialog
            .open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(
                ConfirmModalComponent,
                {
                    width: '574px',
                    autoFocus: false,
                    backdropClass: 'back-drop',
                    data: {
                        title: 'BOOK_PRESENTATION.TITLE',
                        label: 'BOOK_PRESENTATION.SUBTITLE',
                        cancelLabel: 'COMMON.CANCEL',
                        confirmLabel: 'BOOK_PRESENTATION.BOOK',
                        twoAnswers: true
                    }
                }
            )
            .afterClosed().subscribe((result: IConfirmModalAnswer) => {
                if (!result.answer) return;
                this.http
                    .post<{ success: boolean }>('/api/v1/users/book-presentation/', {})
                    .subscribe({
                        next: (response: { success: boolean}) => {
                        if (response?.success) {
                            this.snackBarService.setSnackBarNotification({
                                messageText: 'BOOK_PRESENTATION.ACCESS_MESSAGE'
                            });
                        } else {
                            this.snackBarService.setErrorNotification();
                        }
                    },
                    error: () => this.snackBarService.setErrorNotification()
                }); 
            });
    }

	updateDefaults(): void {
		localStorage.setItem('defaults', JSON.stringify(this.defaults));
		this.setPermissions();
	}

	updateLanguage(language: Languages): Observable<void> {
		return this.http.patch<void>('/api/v1/users/lang/', { lang: language });
	}

	updatePersonalSettings(settings: Partial<IPersonalSettings>): Observable<IPersonalSettings> {
		return this.http
			.patch<IPersonalSettings>('/api/v1/users/personal-settings/', settings)
			.pipe(
				tap((settings: IPersonalSettings) => {
					this.defaults.personal_settings = settings;
					this.personalSettings = new PersonalSettings(settings);
					this.updateDefaults();
				}),
				catchError(() => {
					this.snackBarService.setErrorNotification();
					return of(this.personalSettings.settings);
				})
			);
	}

	updateSettings(): void {
		this.http
			.put('/api/user-config/app/', { ...this.settings })
			.subscribe(() => {
				this.defaults.settings = this.settings;
				this.updateDefaults();
			});
	}

	updateSettings$(): Observable<any> {
		return this.http
			.put('/api/user-config/app/', {...this.settings})
			.pipe(
				tap(() => {
					this.defaults.settings = this.settings;
					this.updateDefaults();
				})
			);
	}
}
